/**
 * Guardrails Management
 * Manage the 10 guardrail rules
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2, Save, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useGuardrails, useUpdateGuardrail, type GuardrailRule } from '@/hooks/use-admin-diagnostic';

export const Route = createFileRoute('/admin/diagnostic/guardrails')({
  component: GuardrailsManagement,
});

function GuardrailsManagement() {
  const { data: guardrails = [], isLoading } = useGuardrails();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<GuardrailRule>>({});

  const updateGuardrail = useUpdateGuardrail();

  const startEdit = (guardrail: GuardrailRule) => {
    setEditingId(guardrail.id);
    setEditForm(guardrail);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editingId || !editForm) return;

    try {
      await updateGuardrail.mutateAsync({
        id: editingId,
        input: editForm,
      });
      setEditingId(null);
      setEditForm({});
    } catch (error) {
      toast.error('Failed to update guardrail');
    }
  };

  return (
    <div className="h-full overflow-y-auto">
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Guardrail Rules</h1>
        <p className="text-muted-foreground mt-1">
          Manage the 10 guardrail rules that prevent invalid lifecycle stages
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-4">
          {guardrails.map((guardrail) => {
            const isEditing = editingId === guardrail.id;

            return (
              <Card key={guardrail.id} className={isEditing ? 'border-primary' : ''}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-primary" />
                      <div>
                        <CardTitle>
                          {isEditing ? (
                            <Input
                              value={editForm.name || ''}
                              onChange={(e) =>
                                setEditForm({ ...editForm, name: e.target.value })
                              }
                              className="mb-2"
                            />
                          ) : (
                            guardrail.name
                          )}
                        </CardTitle>
                        <Badge
                          variant="outline"
                          className="font-mono text-xs mt-1"
                        >
                          {guardrail.code}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`active-${guardrail.id}`} className="text-sm">
                          Active
                        </Label>
                        <Switch
                          id={`active-${guardrail.id}`}
                          checked={isEditing ? editForm.is_active : guardrail.is_active}
                          onCheckedChange={(checked) => {
                            if (isEditing) {
                              setEditForm({ ...editForm, is_active: checked });
                            } else {
                              startEdit({ ...guardrail, is_active: checked });
                            }
                          }}
                        />
                      </div>

                      <Badge variant="secondary">
                        Priority: {isEditing ? editForm.priority : guardrail.priority}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Condition */}
                  <div className="space-y-2">
                    <Label>Condition</Label>
                    {isEditing ? (
                      <Textarea
                        value={editForm.condition || ''}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          setEditForm({ ...editForm, condition: e.target.value })
                        }
                        rows={2}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {guardrail.condition}
                      </p>
                    )}
                  </div>

                  {/* Effect */}
                  <div className="space-y-2">
                    <Label>Effect</Label>
                    {isEditing ? (
                      <Input
                        value={editForm.effect || ''}
                        onChange={(e) =>
                          setEditForm({ ...editForm, effect: e.target.value })
                        }
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">{guardrail.effect}</p>
                    )}
                  </div>

                  {/* Evaluation Logic */}
                  <div className="space-y-2">
                    <Label>Evaluation Logic (JSON)</Label>
                    {isEditing ? (
                      <Textarea
                        value={JSON.stringify(editForm.evaluation_logic, null, 2)}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            setEditForm({ ...editForm, evaluation_logic: parsed });
                          } catch {
                            // Invalid JSON, don't update
                          }
                        }}
                        rows={6}
                        className="font-mono text-xs"
                      />
                    ) : (
                      <pre className="text-xs bg-secondary p-3 rounded-lg overflow-x-auto">
                        {JSON.stringify(guardrail.evaluation_logic, null, 2)}
                      </pre>
                    )}
                  </div>

                  {/* Priority */}
                  {isEditing && (
                    <div className="space-y-2">
                      <Label>Priority (1-10)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={editForm.priority || 1}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            priority: parseInt(e.target.value),
                          })
                        }
                      />
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    {isEditing ? (
                      <>
                        <Button variant="outline" onClick={cancelEdit}>
                          Cancel
                        </Button>
                        <Button
                          onClick={saveEdit}
                          disabled={updateGuardrail.isPending}
                          className="gap-2"
                        >
                          {updateGuardrail.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline" onClick={() => startEdit(guardrail)}>
                        Edit
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
    </div>
  );
}
